import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { centralApi } from '@marque/api/central'
import { confirmDialog } from '@marque/ui'
import type { ModuleBundle, ModuleCatalogItem } from '@marque/api/central'

// AB#1298 — drag-and-drop bundle composer. Left pane = available modules from the catalogue;
// right pane = the bundle's assigned (declared) modules. Dropping a module in persists the new
// declared set; the server returns the effective set (declared + transitive dependsOn closure),
// and dependencies pulled in are shown as locked "required" chips that can't be removed while a
// dependent remains.

function NewBundleForm({ onCreated }: { onCreated: (key: string) => void }) {
    const qc = useQueryClient()
    const [key, setKey] = useState('')
    const [name, setName] = useState('')
    // AB#2799 — standalone product bundle: plain dependsOn closure, no AB#2282 core-set forcing.
    const [standalone, setStandalone] = useState(false)
    // AB#2806 — bundle-declared first-admin role; provisioning uses it when the operator gives none.
    const [bootstrapRoleCode, setBootstrapRoleCode] = useState('')
    // AB#2812 — bundle-declared pricing data source; same provisioning precedence as the role.
    const [pricingDataSource, setPricingDataSource] = useState('')

    const create = useMutation({
        mutationFn: () => centralApi.createBundle({
            key: key.trim(),
            name: name.trim(),
            standalone,
            bootstrapRoleCode: bootstrapRoleCode.trim() || null,
            pricingDataSource: pricingDataSource.trim() || null,
        }),
        onSuccess: b => {
            qc.invalidateQueries({ queryKey: ['bundles'] })
            setKey(''); setName(''); setStandalone(false); setBootstrapRoleCode(''); setPricingDataSource('')
            onCreated(b.key)
        },
    })

    return (
        // AB#1305 — the kebab-case hint sat inside the Key wrapper, breaking the items-end row
        // alignment (Key input rode higher than Name + button). Hint moved into the label so all
        // three controls share one baseline.
        <div className="flex items-end gap-3 flex-wrap">
            <div>
                <label className="block text-xs text-neutral-500 mb-0.5">
                    Key <span className="text-[10px] text-neutral-400">· lower-kebab-case</span>
                </label>
                <input
                    data-testid="bundle-key-input"
                    value={key}
                    // Normalise as the user types: lower-case, spaces/underscores → hyphens, strip
                    // anything outside [a-z0-9-]. Matches the server's lower-kebab-case rule.
                    onChange={e => setKey(
                        e.target.value.toLowerCase().replace(/[\s_]+/g, '-').replace(/[^a-z0-9-]/g, ''),
                    )}
                    placeholder="professional"
                    className="border border-neutral-300 rounded px-2 py-1.5 text-sm w-40"
                />
            </div>
            <div>
                <label className="block text-xs text-neutral-500 mb-0.5">Name</label>
                <input
                    data-testid="bundle-name-input"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Professional"
                    className="border border-neutral-300 rounded px-2 py-1.5 text-sm w-48"
                />
            </div>
            <label className="flex items-center gap-1.5 text-sm text-neutral-700 cursor-pointer pb-2" title="A standalone product bundle entitles exactly its declared modules plus their dependencies — the core module set is never forced in. Use for single-product tenants (e.g. pricing-service).">
                <input
                    data-testid="bundle-standalone-input"
                    type="checkbox"
                    checked={standalone}
                    onChange={e => setStandalone(e.target.checked)}
                    className="rounded"
                />
                Standalone product bundle
            </label>
            <div title="Role auto-assigned to the FIRST admin of a tenant provisioned against this bundle (an explicit role on the provision form still wins). Blank = the tenant-owner default.">
                <label className="block text-xs text-neutral-500 mb-0.5">
                    Bootstrap role <span className="text-[10px] text-neutral-400">· optional</span>
                </label>
                <input
                    data-testid="bundle-bootstrap-role-input"
                    value={bootstrapRoleCode}
                    // Same lower-kebab-case normalisation as the key — matches the server rule.
                    onChange={e => setBootstrapRoleCode(
                        e.target.value.toLowerCase().replace(/[\s_]+/g, '-').replace(/[^a-z0-9-]/g, ''),
                    )}
                    placeholder="pricing-admin"
                    className="border border-neutral-300 rounded px-2 py-1.5 text-sm w-40"
                />
            </div>
            <div title="Pricing data source stamped on tenants provisioned against this bundle (an explicit value on the provision form still wins). Blank = the marque-entity-records default; request-only disables fact resolution.">
                <label className="block text-xs text-neutral-500 mb-0.5">
                    Pricing data source <span className="text-[10px] text-neutral-400">· optional</span>
                </label>
                <input
                    data-testid="bundle-pricing-data-source-input"
                    value={pricingDataSource}
                    // Same lower-kebab-case normalisation as the key — matches the server rule.
                    onChange={e => setPricingDataSource(
                        e.target.value.toLowerCase().replace(/[\s_]+/g, '-').replace(/[^a-z0-9-]/g, ''),
                    )}
                    placeholder="request-only"
                    className="border border-neutral-300 rounded px-2 py-1.5 text-sm w-40"
                />
            </div>
            <button
                data-testid="bundle-create"
                onClick={() => create.mutate()}
                disabled={create.isPending || !key.trim() || !name.trim()}
                title={(!key.trim() || !name.trim()) ? 'Enter a key and a name to enable' : undefined}
                className="text-sm bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50"
            >
                {create.isPending ? 'Creating…' : '+ New bundle'}
            </button>
            {create.isError && (
                <span className="text-xs text-red-600 max-w-xs self-center" data-testid="bundle-create-error">
                    {create.error instanceof Error ? create.error.message : 'Create failed'}
                </span>
            )}
        </div>
    )
}

function BundleComposer({ bundle, catalog }: { bundle: ModuleBundle; catalog: ModuleCatalogItem[] }) {
    const qc = useQueryClient()
    const [dragOver, setDragOver] = useState(false)

    const setModules = useMutation({
        mutationFn: (moduleIds: string[]) => centralApi.setBundleModules(bundle.key, moduleIds),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['bundles'] }),
    })

    // AB#1592 — delete a bundle (cascades its module assignments; already-provisioned tenants keep
    // their entitlements). Confirm first since it's irreversible.
    const deleteBundle = useMutation({
        mutationFn: () => centralApi.deleteBundle(bundle.key),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['bundles'] }),
    })

    const declared = bundle.declaredModules
    const declaredSet = useMemo(() => new Set(declared), [declared])
    // Dependencies pulled in by the closure but not explicitly declared — shown as locked chips.
    const dependencyOnly = bundle.effectiveModules.filter(m => !declaredSet.has(m))

    const available = catalog
        .filter(m => !declaredSet.has(m.moduleId))
        .sort((a, b) => a.sortOrder - b.sortOrder)

    const nameOf = (id: string) => catalog.find(m => m.moduleId === id)?.name ?? id

    function addModule(moduleId: string) {
        if (declaredSet.has(moduleId)) { return }
        setModules.mutate([...declared, moduleId])
    }

    function removeModule(moduleId: string) {
        setModules.mutate(declared.filter(m => m !== moduleId))
    }

    return (
        <div className="border border-neutral-200 rounded-lg p-4" data-testid={`bundle-${bundle.key}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div>
                    <h3 className="text-sm font-semibold text-neutral-900">
                        {bundle.name} <code className="text-xs text-neutral-400">{bundle.key}</code>
                        {/* AB#2799 — standalone product bundle: no core-set forcing. */}
                        {bundle.standalone && (
                            <span
                                data-testid={`standalone-${bundle.key}`}
                                className="ml-2 text-[10px] uppercase tracking-wide text-purple-600 border border-purple-200 bg-purple-50 rounded px-1 py-0.5"
                                title="Standalone product bundle — entitles exactly its declared modules plus dependencies; the core module set is never forced in."
                            >standalone</span>
                        )}
                        {/* AB#2806 — bundle-declared first-admin role (provisioning default). */}
                        {bundle.bootstrapRoleCode && (
                            <span
                                data-testid={`bootstrap-role-${bundle.key}`}
                                className="ml-2 text-[10px] uppercase tracking-wide text-teal-700 border border-teal-200 bg-teal-50 rounded px-1 py-0.5"
                                title="Role auto-assigned to the first admin of a tenant provisioned against this bundle (an explicit role on the provision form still wins)."
                            >role: {bundle.bootstrapRoleCode}</span>
                        )}
                        {/* AB#2812 — bundle-declared pricing data source (provisioning default). */}
                        {bundle.pricingDataSource && (
                            <span
                                data-testid={`pricing-data-source-${bundle.key}`}
                                className="ml-2 text-[10px] uppercase tracking-wide text-indigo-700 border border-indigo-200 bg-indigo-50 rounded px-1 py-0.5"
                                title="Pricing data source stamped on tenants provisioned against this bundle (an explicit value on the provision form still wins). request-only = no fact resolution."
                            >facts: {bundle.pricingDataSource}</span>
                        )}
                    </h3>
                    {!bundle.isActive && <span className="text-xs text-amber-600">inactive</span>}
                </div>
                <div className="flex items-center gap-3">
                    {setModules.isPending && <span className="text-xs text-neutral-400">Saving…</span>}
                    <button
                        onClick={() => { void confirmDialog(`Delete bundle "${bundle.name}"? Its module assignments are removed. Tenants already provisioned with this bundle keep their entitlements.`, { title: 'Delete bundle', danger: true, confirmLabel: 'Delete' }).then(ok => { if (ok) { deleteBundle.mutate() } }) }}
                        disabled={deleteBundle.isPending}
                        data-testid={`delete-bundle-${bundle.key}`}
                        className="text-xs text-red-600 hover:underline disabled:opacity-50"
                        title="Delete bundle"
                    >Delete</button>
                </div>
            </div>

            {/* AB#1305 — symmetric panes (both bordered boxes), min-w-0 + truncation so long module
                ids can't blow the columns apart. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
                {/* Available modules — draggable source */}
                <div className="rounded border border-neutral-200 p-2 min-h-[88px] min-w-0">
                    <p className="text-xs font-medium text-neutral-500 mb-1.5">Available modules</p>
                    <div className="space-y-1">
                        {available.length === 0 && (
                            <p className="text-xs text-neutral-400 italic">All catalogue modules assigned.</p>
                        )}
                        {available.map(m => (
                            <div
                                key={m.moduleId}
                                draggable
                                onDragStart={e => e.dataTransfer.setData('text/plain', m.moduleId)}
                                onDoubleClick={() => addModule(m.moduleId)}
                                data-testid={`available-${m.moduleId}`}
                                className="flex items-center gap-2 bg-neutral-50 border border-neutral-200 rounded px-2 py-1 text-sm cursor-grab hover:bg-neutral-100 min-w-0"
                            >
                                <span className="flex-1 min-w-0 truncate">{m.name} <code className="text-xs text-neutral-400">{m.moduleId}</code></span>
                                <button
                                    onClick={() => addModule(m.moduleId)}
                                    className="shrink-0 text-xs text-blue-600 hover:underline"
                                    title="Add to bundle"
                                >+</button>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Bundle drop zone — assigned + dependency chips */}
                <div
                    onDragOver={e => { e.preventDefault(); setDragOver(true) }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={e => {
                        e.preventDefault()
                        setDragOver(false)
                        const id = e.dataTransfer.getData('text/plain')
                        if (id) { addModule(id) }
                    }}
                    data-testid={`dropzone-${bundle.key}`}
                    className={`rounded border-2 border-dashed p-2 min-h-[88px] min-w-0 transition-colors ${
                        dragOver ? 'border-blue-400 bg-blue-50' : 'border-neutral-200'
                    }`}
                >
                    <p className="text-xs font-medium text-neutral-500 mb-1.5">In this bundle</p>
                    <div className="space-y-1">
                        {declared.length === 0 && dependencyOnly.length === 0 && (
                            <p className="text-xs text-neutral-400 italic">Drag modules here.</p>
                        )}
                        {declared.map(id => (
                            <div
                                key={id}
                                data-testid={`assigned-${id}`}
                                className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded px-2 py-1 text-sm min-w-0"
                            >
                                <span className="flex-1 min-w-0 truncate">{nameOf(id)} <code className="text-xs text-blue-400">{id}</code></span>
                                <button
                                    onClick={() => removeModule(id)}
                                    className="shrink-0 text-xs text-red-500 hover:text-red-700"
                                    title="Remove"
                                >×</button>
                            </div>
                        ))}
                        {/* Locked dependency chips — pulled in by the closure, not removable directly. */}
                        {dependencyOnly.map(id => (
                            <div
                                key={id}
                                data-testid={`dependency-${id}`}
                                className="flex items-center gap-2 bg-neutral-100 border border-neutral-200 rounded px-2 py-1 text-sm text-neutral-500 min-w-0"
                                title="Required by another module in this bundle"
                            >
                                <span className="flex-1 min-w-0 truncate">{nameOf(id)} <code className="text-xs text-neutral-400">{id}</code></span>
                                <span className="shrink-0 text-[10px] uppercase tracking-wide">required</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {bundle.missingDependencies.length > 0 && (
                <p className="mt-2 text-xs text-red-600">
                    Missing dependencies (not in catalogue): {bundle.missingDependencies.join(', ')}
                </p>
            )}
        </div>
    )
}

export function BundlesPage() {
    const bundlesQuery = useQuery({ queryKey: ['bundles'], queryFn: () => centralApi.listBundles() })
    const catalogQuery = useQuery({ queryKey: ['module-catalog'], queryFn: () => centralApi.listModuleCatalog() })

    const bundles = bundlesQuery.data ?? []
    const catalog = catalogQuery.data ?? []

    return (
        <div className="p-6 max-w-5xl">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-2xl font-semibold text-neutral-900">Module Bundles</h1>
                    <p className="text-sm text-neutral-500">
                        Commercial plans (Basic / Professional / Enterprise). Drag modules into a bundle;
                        dependencies are pulled in automatically.
                    </p>
                </div>
            </div>

            <div className="mb-6">
                <NewBundleForm onCreated={() => {/* list refetches via invalidation */}} />
            </div>

            {(bundlesQuery.isLoading || catalogQuery.isLoading) && (
                <p className="text-sm text-neutral-500">Loading…</p>
            )}

            {bundles.length === 0 && !bundlesQuery.isLoading && (
                <p className="text-sm text-neutral-400 italic">No bundles yet. Create one above.</p>
            )}

            <div className="space-y-4">
                {bundles.map(b => (
                    <BundleComposer key={b.key} bundle={b} catalog={catalog} />
                ))}
            </div>
        </div>
    )
}
